import type { Menu } from "../service/menu/types";
import {
  normalizeMenuPath,
  PREMIUM_SERIES_INNER_BASE,
  PREMIUM_TRANSLATION_FULL_PATH,
  resolveMenuNavigationPath,
} from "./menuPathNormalize";

export interface SidebarSection {
  /** 分区标题，如"项目管理"、"系统管理" */
  label: string;
  /** 分区根菜单节点（保留 icon 等信息供扩展使用） */
  root: Menu.Menu;
  /** 该分区下的可见子菜单（已排序、过滤） */
  children: Menu.Menu[];
}

export function findMenuById(menus: Menu.Menu[], id: number): Menu.Menu | null {
  for (const m of menus) {
    if (m.id === id) return m;
    if (m.children?.length) {
      const found = findMenuById(m.children, id);
      if (found) return found;
    }
  }
  return null;
}

export function getMenuDisplayTitle(m: Menu.Menu): string {
  return m.meta?.title || m.name;
}

export function sortVisibleMenus(menus: Menu.Menu[]): Menu.Menu[] {
  return [...menus]
    .filter((m) => !m.hidden)
    .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
}

/** 过滤不应在侧栏展示/匹配的菜单项 */
function shouldSkipMenuItem(m: Menu.Menu): boolean {
  const title = getMenuDisplayTitle(m);
  // 隐藏"开发中"菜单
  if (title.includes("开发中")) return true;
  return false;
}

/** Ant Design SubMenu 稳定 key（与 path 解耦，避免目录节点无 path 时冲突） */
export function homeShellDirMenuKey(m: Menu.Menu): string {
  return m.id != null ? `dir-${m.id}` : `dir-${encodeURIComponent(m.name)}`;
}

/** 可路由叶子项在 Menu 中的 key：内部路由为规范化 path，外链为完整 URL */
export function homeShellLeafMenuKey(m: Menu.Menu): string | null {
  const p = (m.path || "").trim();
  if (!p) return null;
  if (isExternalMenuPath(p)) return p;
  return resolvedHomeShellHref(m);
}

/**
 * 与 Profile 侧栏一致：仅展示可跳转项。
 * 按钮类型（type === 3）不参与导航；无 path 的目录展开其子项。
 */
export function flattenHomeShellNavItems(roots: Menu.Menu[]): Menu.Menu[] {
  const out: Menu.Menu[] = [];
  for (const m of sortVisibleMenus(roots)) {
    if (shouldSkipMenuItem(m)) continue;
    // 跳过按钮类型（type===3）
    if (m.type === 3) continue;
    const p = (m.path || "").trim();
    const external = /^https?:\/\//i.test(p);
    if (p && (m.component || external)) {
      out.push(m);
    } else if (m.children?.length) {
      out.push(...flattenHomeShellNavItems(m.children));
    }
  }
  return out;
}

export function resolvedHomeShellHref(m: Menu.Menu): string {
  return resolveMenuNavigationPath((m.path || "").trim(), m.component);
}

export function isExternalMenuPath(path: string): boolean {
  return /^https?:\/\//i.test(path.trim());
}

export function getFirstHomeShellNavigationPath(menus: Menu.Menu[]): string | null {
  const sections = buildSidebarSections(menus);
  for (const section of sections) {
    const items = flattenHomeShellNavItems(section.children);
    for (const item of items) {
      const href = homeShellLeafMenuKey(item);
      if (href && !isExternalMenuPath(href)) return href;
    }
  }
  return null;
}

/**
 * 构建侧边栏分区列表：遍历顶级菜单节点，对有可见子菜单的节点生成 SidebarSection。
 * 不再依赖标题字符串匹配或硬编码 ID，完全由后端菜单树结构驱动。
 */
export function buildSidebarSections(menus: Menu.Menu[]): SidebarSection[] {
  const sections: SidebarSection[] = [];
  for (const root of sortVisibleMenus(menus)) {
    if (shouldSkipMenuItem(root)) continue;
    // 按钮类型不独立成区
    if (root.type === 3) continue;
    // 有两种情况需要渲染为一个 section：
    // 1. 有 children 的目录节点（type===1 或 fallback）
    // 2. 自身是可跳转的叶子节点（type===2），也作为独立 section
    const hasVisibleChildren = root.children?.length
      ? sortVisibleMenus(root.children).filter((c) => !shouldSkipMenuItem(c) && c.type !== 3).length > 0
      : false;
    if (hasVisibleChildren) {
      sections.push({
        label: getMenuDisplayTitle(root),
        root,
        children: sortVisibleMenus(root.children).filter(
          (c) => !shouldSkipMenuItem(c) && c.type !== 3
        ),
      });
    } else if (root.path && root.component) {
      // 自身是叶子菜单，也生成一个 section（无 label 或单条）
      sections.push({
        label: getMenuDisplayTitle(root),
        root,
        children: [root],
      });
    }
  }
  return sections;
}

/**
 * 当前路由在侧边栏应高亮的菜单 key，及需展开的 SubMenu keys。
 * 接受合并后的扁平菜单列表（所有 section 的 children），不再依赖分区结构。
 */
export function resolveHomeShellMenuSelection(
  pathname: string,
  allMenus: Menu.Menu[]
): { selected: string | null; openKeys: string[] } | null {
  if (
    pathname === PREMIUM_SERIES_INNER_BASE ||
    pathname.startsWith(`${PREMIUM_SERIES_INNER_BASE}/`)
  ) {
    return { selected: PREMIUM_TRANSLATION_FULL_PATH, openKeys: [] };
  }

  /** 与菜单 path 同一套规范化，避免历史子 path（如 /hub/voice-library/design）误匹配「音色库」前缀 */
  const pathnameResolved = normalizeMenuPath(pathname);

  const walk = (
    list: Menu.Menu[],
    stack: string[]
  ): { selected: string | null; openKeys: string[] } | null => {
    for (const menu of sortVisibleMenus(list)) {
      if (shouldSkipMenuItem(menu)) continue;

      const children = menu.children?.length ? sortVisibleMenus(menu.children) : [];
      if (children.length > 0) {
        const dk = homeShellDirMenuKey(menu);
        const hit = walk(children, [...stack, dk]);
        if (hit) return hit;
      }

      const lk = homeShellLeafMenuKey(menu);
      if (!lk || isExternalMenuPath(menu.path || "")) continue;
      const norm = lk;
      if (pathnameResolved === norm || pathnameResolved.startsWith(`${norm}/`)) {
        return { selected: norm, openKeys: stack };
      }
    }
    return null;
  };

  return walk(allMenus, []);
}
